import { zodResolver } from "@hookform/resolvers/zod";
import {
  META_ASSET_TASKS,
  META_ASSET_TYPE_LABELS,
  META_BUSINESS_ROLE_LABELS,
  META_INVITABLE_BUSINESS_ROLES,
  META_PARTNER_AD_ACCOUNT_TASKS,
  META_SYSTEM_USER_ROLES,
  addPartnerSchema,
  createSystemUserSchema,
  inviteBusinessUserSchema,
  metaTaskLabel,
  roleHas,
  type AddPartnerInput,
  type BusinessAssetDto,
  type BusinessDetailDto,
  type BusinessPartnerDto,
  type BusinessUserDto,
  type CreateSystemUserInput,
  type InviteBusinessUserInput,
  type SystemUserDto,
  type SystemUserTokenResultDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  Handshake,
  KeyRound,
  Layers,
  MailPlus,
  MoreHorizontal,
  Plus,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { ClientTag } from "@/components/client-filter";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { CreateAdAccountDialog } from "@/components/meta/ad-account-dialogs";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { formatDate, verificationVariant } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/business-managers_/$businessId")({
  component: BusinessDetailPage,
});

type Tab = "pessoas" | "parceiros" | "system-users" | "ativos";

function BusinessDetailPage() {
  const { workspaceId, businessId } = Route.useParams();
  const workspace = useQuery(queries.workspace(workspaceId));
  const detail = useQuery(queries.businessDetail(workspaceId, businessId));
  const clients = useQuery(queries.clients(workspaceId));
  const [tab, setTab] = useState<Tab>("pessoas");
  const [criarConta, setCriarConta] = useState(false);
  const role = workspace.data?.role;
  const podeGerenciar = role ? roleHas(role, "bm:manage") : false;
  const podeToken = role ? roleHas(role, "meta:manage") : false;

  if (detail.isPending) return <LoadingState rows={5} />;
  if (detail.isError)
    return (
      <>
        <Button asChild variant="ghost" size="sm" className="mb-4">
          <Link to="/app/$workspaceId/business-managers" params={{ workspaceId }}>
            <ArrowLeft /> Business Managers
          </Link>
        </Button>
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      </>
    );

  const d = detail.data;
  const isAdmin = d.business.permittedRoles.includes("ADMIN");

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
        <Link to="/app/$workspaceId/business-managers" params={{ workspaceId }}>
          <ArrowLeft /> Business Managers
        </Link>
      </Button>
      <PageHeader
        title={d.business.name}
        description={`id ${d.business.metaId} · conexão ${d.business.connectionName}`}
        actions={
          podeGerenciar && isAdmin ? (
            <Button onClick={() => setCriarConta(true)}>
              <Plus />
              Criar conta
            </Button>
          ) : null
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <Badge variant={verificationVariant(d.business.verificationStatus)}>
          {d.business.verificationLabel}
        </Badge>
        <Badge variant="outline">Seu papel: {d.business.roleLabel}</Badge>
        <span className="text-muted-foreground">Cliente:</span>
        <ClientTag clientId={d.clientId} clients={clients.data} />
      </div>
      {!isAdmin ? (
        <Alert className="mb-4">
          <AlertTriangle />
          <AlertTitle>Só leitura neste BM</AlertTitle>
          <AlertDescription>
            A conta Meta conectada não é administradora deste Business Manager; a Meta só deixa
            administradores convidar pessoas, criar contas e system users.
          </AlertDescription>
        </Alert>
      ) : null}
      {d.warnings.length > 0 ? (
        <Alert className="mb-4">
          <AlertTriangle />
          <AlertTitle>Partes que a Meta não deixou ler</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {d.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="pessoas">
            <Users className="size-4" aria-hidden /> Pessoas ({d.users.length})
          </TabsTrigger>
          <TabsTrigger value="parceiros">
            <Handshake className="size-4" aria-hidden /> Parceiros ({d.partners.length})
          </TabsTrigger>
          <TabsTrigger value="system-users">
            <Bot className="size-4" aria-hidden /> System users ({d.systemUsers.length})
          </TabsTrigger>
          <TabsTrigger value="ativos">
            <Layers className="size-4" aria-hidden /> Ativos ({d.assets.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pessoas">
          <PeopleTab
            detail={d}
            workspaceId={workspaceId}
            businessId={businessId}
            podeGerenciar={podeGerenciar && isAdmin}
          />
        </TabsContent>
        <TabsContent value="parceiros">
          <PartnersTab
            detail={d}
            workspaceId={workspaceId}
            businessId={businessId}
            podeGerenciar={podeGerenciar && isAdmin}
          />
        </TabsContent>
        <TabsContent value="system-users">
          <SystemUsersTab
            detail={d}
            workspaceId={workspaceId}
            businessId={businessId}
            podeGerenciar={podeGerenciar && isAdmin}
            podeToken={podeToken && isAdmin}
          />
        </TabsContent>
        <TabsContent value="ativos">
          <AssetsTab
            detail={d}
            workspaceId={workspaceId}
            businessId={businessId}
            podeGerenciar={podeGerenciar && isAdmin}
          />
        </TabsContent>
      </Tabs>
      <CreateAdAccountDialog
        open={criarConta}
        onOpenChange={setCriarConta}
        workspaceId={workspaceId}
        business={d.business}
      />
    </>
  );
}

function useDetailInvalidate(workspaceId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "meta"] });
}

// ---- Pessoas -----------------------------------------------------------------------------

function PeopleTab({
  detail,
  workspaceId,
  businessId,
  podeGerenciar,
}: {
  detail: BusinessDetailDto;
  workspaceId: string;
  businessId: string;
  podeGerenciar: boolean;
}) {
  const invalidate = useDetailInvalidate(workspaceId);
  const base = `/api/workspaces/${workspaceId}/meta/businesses/${businessId}`;
  const [convidar, setConvidar] = useState(false);
  const [remover, setRemover] = useState<BusinessUserDto | null>(null);
  const [mudarPapel, setMudarPapel] = useState<{ user: BusinessUserDto; role: string } | null>(
    null,
  );

  const removerMut = useMutation({
    mutationFn: (u: BusinessUserDto) =>
      api(`${base}/users/${u.id}${u.pending ? "?pendente=1" : ""}`, { method: "DELETE" }),
    onSuccess: async (_, u) => {
      await invalidate();
      setRemover(null);
      toast.success(u.pending ? "Convite cancelado na Meta." : "Pessoa removida do BM na Meta.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const papelMut = useMutation({
    mutationFn: (input: { user: BusinessUserDto; role: string }) =>
      api(`${base}/users/${input.user.id}`, { method: "PATCH", body: { role: input.role } }),
    onSuccess: async () => {
      await invalidate();
      setMudarPapel(null);
      toast.success("Papel alterado na Meta.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            Pessoas com acesso ao BM e convites pendentes (lidos ao vivo da Meta).
          </p>
          {podeGerenciar ? (
            <Button size="sm" onClick={() => setConvidar(true)}>
              <MailPlus />
              Convidar pessoa
            </Button>
          ) : null}
        </div>
        {detail.users.length === 0 ? (
          <EmptyState
            title="Nenhuma pessoa"
            description="A Meta não devolveu pessoas para este BM."
          />
        ) : (
          <ul className="divide-y">
            {detail.users.map((u) => (
              <li key={u.id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{u.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {u.email ?? "sem e-mail"} · id {u.id}
                    {u.pending && u.invitedAt ? ` · convidado em ${formatDate(u.invitedAt)}` : ""}
                    {u.pending && u.expiresAt ? ` · expira em ${formatDate(u.expiresAt)}` : ""}
                  </p>
                </div>
                <Badge variant={u.pending ? "warning" : "secondary"}>
                  {u.pending ? `Convite pendente · ${u.roleLabel}` : u.roleLabel}
                </Badge>
                {podeGerenciar ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={`Ações para ${u.name}`}>
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!u.pending ? (
                        <>
                          <DropdownMenuLabel>Mudar papel</DropdownMenuLabel>
                          {META_INVITABLE_BUSINESS_ROLES.map((r) => (
                            <DropdownMenuItem
                              key={r}
                              disabled={r === u.role}
                              onSelect={() => setMudarPapel({ user: u, role: r })}
                            >
                              {META_BUSINESS_ROLE_LABELS[r]}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      <DropdownMenuItem variant="destructive" onSelect={() => setRemover(u)}>
                        {u.pending ? "Cancelar convite" : "Remover do BM"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <InviteDialog open={convidar} onOpenChange={setConvidar} base={base} onDone={invalidate} />
      <ConfirmDialog
        open={remover !== null}
        onOpenChange={(v) => {
          if (!v) setRemover(null);
        }}
        title={
          remover?.pending
            ? `Cancelar o convite de ${remover.email ?? ""}?`
            : `Remover ${remover?.name ?? ""} do BM?`
        }
        description={
          remover?.pending
            ? "O convite deixa de valer na Meta."
            : "A pessoa perde o acesso ao Business Manager e a todos os ativos dele na Meta."
        }
        confirmLabel={remover?.pending ? "Cancelar convite" : "Remover na Meta"}
        destructive
        loading={removerMut.isPending}
        onConfirm={() => {
          if (remover) removerMut.mutate(remover);
        }}
      />
      <ConfirmDialog
        open={mudarPapel !== null}
        onOpenChange={(v) => {
          if (!v) setMudarPapel(null);
        }}
        title={`Mudar o papel de ${mudarPapel?.user.name ?? ""}?`}
        description={`De ${mudarPapel?.user.roleLabel ?? ""} para ${
          mudarPapel ? (META_BUSINESS_ROLE_LABELS as Record<string, string>)[mudarPapel.role] : ""
        }, na Meta.`}
        confirmLabel="Mudar na Meta"
        loading={papelMut.isPending}
        onConfirm={() => {
          if (mudarPapel) papelMut.mutate(mudarPapel);
        }}
      />
    </Card>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  base,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  base: string;
  onDone: () => Promise<unknown>;
}) {
  const form = useForm<InviteBusinessUserInput>({
    resolver: zodResolver(inviteBusinessUserSchema),
    defaultValues: { email: "", role: "EMPLOYEE" },
  });
  const role = useWatch({ control: form.control, name: "role" });
  const convidar = useMutation({
    mutationFn: (input: InviteBusinessUserInput) =>
      api(`${base}/users`, { method: "POST", body: input }),
    onSuccess: async (_, input) => {
      await onDone();
      toast.success(`Convite enviado pela Meta para ${input.email}.`);
      form.reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => convidar.mutate(v))}
          noValidate
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Convidar pessoa para o BM</DialogTitle>
            <DialogDescription>
              A Meta envia o convite por e-mail; ele fica pendente até ser aceito.
            </DialogDescription>
          </DialogHeader>
          <FormField
            id="bm-invite-email"
            label="E-mail"
            error={form.formState.errors.email?.message}
          >
            <Input
              id="bm-invite-email"
              type="email"
              autoFocus
              placeholder="pessoa@empresa.com"
              {...form.register("email")}
            />
          </FormField>
          <FormField
            id="bm-invite-role"
            label="Papel no BM"
            error={form.formState.errors.role?.message}
          >
            <Select
              value={role}
              onValueChange={(v) =>
                form.setValue("role", v as InviteBusinessUserInput["role"], {
                  shouldValidate: true,
                })
              }
            >
              <SelectTrigger id="bm-invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {META_INVITABLE_BUSINESS_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {META_BUSINESS_ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={convidar.isPending}>
              Enviar convite pela Meta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Parceiros ---------------------------------------------------------------------------

function PartnersTab({
  detail,
  workspaceId,
  businessId,
  podeGerenciar,
}: {
  detail: BusinessDetailDto;
  workspaceId: string;
  businessId: string;
  podeGerenciar: boolean;
}) {
  const invalidate = useDetailInvalidate(workspaceId);
  const base = `/api/workspaces/${workspaceId}/meta/businesses/${businessId}`;
  const [adicionar, setAdicionar] = useState(false);
  const [remover, setRemover] = useState<BusinessPartnerDto | null>(null);
  const removerMut = useMutation({
    mutationFn: (p: BusinessPartnerDto) => api(`${base}/partners/${p.id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await invalidate();
      setRemover(null);
      toast.success("Parceiro removido na Meta.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const accountName = (metaId: string) =>
    detail.assets.find((a) => a.metaId === metaId)?.name ?? metaId;

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            Agências que acessam ativos deste BM e BMs de clientes cujos ativos este BM acessa.
          </p>
          {podeGerenciar ? (
            <Button size="sm" onClick={() => setAdicionar(true)}>
              <Plus />
              Adicionar parceiro
            </Button>
          ) : null}
        </div>
        {detail.partners.length === 0 ? (
          <EmptyState
            title="Nenhum parceiro"
            description="Nenhum BM parceiro compartilha ativos com este, nem o contrário."
          />
        ) : (
          <ul className="divide-y">
            {detail.partners.map((p) => (
              <li key={`${p.relation}-${p.id}`} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="text-xs text-muted-foreground">
                    id {p.id} ·{" "}
                    {p.adAccountMetaIds.length > 0
                      ? `contas: ${p.adAccountMetaIds.map(accountName).join(", ")}`
                      : "sem contas compartilhadas"}
                    {p.pageMetaIds.length > 0 ? ` · ${p.pageMetaIds.length} página(s)` : ""}
                  </p>
                </div>
                <Badge variant={p.relation === "agencia" ? "secondary" : "outline"}>
                  {p.relation === "agencia" ? "Agência parceira" : "Cliente"}
                </Badge>
                {podeGerenciar && p.relation === "agencia" ? (
                  <Button variant="ghost" size="sm" onClick={() => setRemover(p)}>
                    Remover
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <AddPartnerDialog
        open={adicionar}
        onOpenChange={setAdicionar}
        base={base}
        detail={detail}
        onDone={invalidate}
      />
      <ConfirmDialog
        open={remover !== null}
        onOpenChange={(v) => {
          if (!v) setRemover(null);
        }}
        title={`Remover o parceiro ${remover?.name ?? ""}?`}
        description="O BM parceiro perde o acesso a todos os ativos que este BM compartilhava com ele, na Meta."
        confirmLabel="Remover na Meta"
        destructive
        loading={removerMut.isPending}
        onConfirm={() => {
          if (remover) removerMut.mutate(remover);
        }}
      />
    </Card>
  );
}

function AddPartnerDialog({
  open,
  onOpenChange,
  base,
  detail,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  base: string;
  detail: BusinessDetailDto;
  onDone: () => Promise<unknown>;
}) {
  const accounts = detail.assets.filter((a) => a.type === "ad_account");
  const form = useForm<AddPartnerInput>({
    resolver: zodResolver(addPartnerSchema),
    defaultValues: {
      partnerBusinessId: "",
      adAccountId: accounts[0]?.metaId ?? "",
      tasks: ["ADVERTISE", "ANALYZE"],
    },
  });
  const adAccountId = useWatch({ control: form.control, name: "adAccountId" });
  const tasks = useWatch({ control: form.control, name: "tasks" });
  const adicionar = useMutation({
    mutationFn: (input: AddPartnerInput) =>
      api(`${base}/partners`, { method: "POST", body: input }),
    onSuccess: async () => {
      await onDone();
      toast.success("Conta compartilhada com o parceiro na Meta.");
      form.reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const toggle = (t: AddPartnerInput["tasks"][number]) =>
    form.setValue("tasks", tasks.includes(t) ? tasks.filter((x) => x !== t) : [...tasks, t], {
      shouldValidate: true,
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => adicionar.mutate(v))}
          noValidate
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Adicionar parceiro</DialogTitle>
            <DialogDescription>
              A Meta não aceita "adicionar parceiro" solto: o vínculo nasce ao compartilhar um
              ativo. Informe o id do BM parceiro e a conta de anúncio que ele vai acessar.
            </DialogDescription>
          </DialogHeader>
          <FormField
            id="partner-id"
            label="Id do Business Manager parceiro"
            error={form.formState.errors.partnerBusinessId?.message}
          >
            <Input
              id="partner-id"
              autoFocus
              placeholder="Ex.: 123456789012345"
              {...form.register("partnerBusinessId")}
            />
          </FormField>
          <FormField
            id="partner-account"
            label="Conta de anúncio a compartilhar"
            error={form.formState.errors.adAccountId?.message}
          >
            <Select
              value={adAccountId}
              onValueChange={(v) => form.setValue("adAccountId", v, { shouldValidate: true })}
            >
              <SelectTrigger id="partner-account">
                <SelectValue placeholder="Escolha a conta" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.metaId} value={a.metaId}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Tarefas permitidas</legend>
            <div className="flex flex-wrap gap-3">
              {META_PARTNER_AD_ACCOUNT_TASKS.map((t) => (
                <label key={t} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={tasks.includes(t)}
                    onChange={() => toggle(t)}
                  />
                  {metaTaskLabel(t)}
                </label>
              ))}
            </div>
            {form.formState.errors.tasks ? (
              <p className="text-sm text-destructive">{form.formState.errors.tasks.message}</p>
            ) : null}
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={adicionar.isPending} disabled={accounts.length === 0}>
              Compartilhar na Meta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- System users --------------------------------------------------------------------------

function SystemUsersTab({
  detail,
  workspaceId,
  businessId,
  podeGerenciar,
  podeToken,
}: {
  detail: BusinessDetailDto;
  workspaceId: string;
  businessId: string;
  podeGerenciar: boolean;
  podeToken: boolean;
}) {
  const invalidate = useDetailInvalidate(workspaceId);
  const base = `/api/workspaces/${workspaceId}/meta/businesses/${businessId}`;
  const [criar, setCriar] = useState(false);
  const [gerarToken, setGerarToken] = useState<SystemUserDto | null>(null);
  const tokenMut = useMutation({
    mutationFn: (su: SystemUserDto) =>
      api<SystemUserTokenResultDto>(`${base}/system-users/${su.id}/token`, { method: "POST" }),
    onSuccess: async (result) => {
      await invalidate();
      setGerarToken(null);
      toast.success(
        `Token gerado e guardado cifrado: conexão "${result.connection.name}" (não expira).`,
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            System users representam integrações. O token deles não expira e pode substituir o token
            de pessoa (60 dias) nas ações deste BM.
          </p>
          {podeGerenciar ? (
            <Button size="sm" onClick={() => setCriar(true)}>
              <Plus />
              Criar system user
            </Button>
          ) : null}
        </div>
        {detail.systemUsers.length === 0 ? (
          <EmptyState
            title="Nenhum system user"
            description="Crie um para gerar um token que não expira."
          />
        ) : (
          <ul className="divide-y">
            {detail.systemUsers.map((s) => (
              <li key={s.id} className="flex items-center gap-3 py-3">
                <Bot className="size-5 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">
                    id {s.id} · {s.roleLabel}
                  </p>
                </div>
                {s.connectionId ? (
                  <Badge variant="success">Token em uso neste workspace</Badge>
                ) : (
                  <Badge variant="secondary">Sem token aqui</Badge>
                )}
                {podeToken ? (
                  <Button variant="outline" size="sm" onClick={() => setGerarToken(s)}>
                    <KeyRound />
                    {s.connectionId ? "Gerar token de novo" : "Gerar token e usar"}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">
          Conexões de system user aparecem em{" "}
          <Link to="/app/$workspaceId/integracoes" params={{ workspaceId }} className="underline">
            Integrações
          </Link>
          .
        </p>
      </CardContent>
      <CreateSystemUserDialog
        open={criar}
        onOpenChange={setCriar}
        base={base}
        onDone={invalidate}
      />
      <ConfirmDialog
        open={gerarToken !== null}
        onOpenChange={(v) => {
          if (!v) setGerarToken(null);
        }}
        title={`Gerar token para ${gerarToken?.name ?? ""}?`}
        description="Instalamos o app para o system user na Meta, geramos um token com as permissões do IAGENIX Ads (ads_read, ads_management, business_management, pages_show_list, pages_read_engagement) e o guardamos cifrado como uma conexão deste workspace. Ele não expira e passa a ser usado nas ações deste BM."
        confirmLabel="Gerar e usar o token"
        loading={tokenMut.isPending}
        onConfirm={() => {
          if (gerarToken) tokenMut.mutate(gerarToken);
        }}
      />
    </Card>
  );
}

function CreateSystemUserDialog({
  open,
  onOpenChange,
  base,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  base: string;
  onDone: () => Promise<unknown>;
}) {
  const form = useForm<CreateSystemUserInput>({
    resolver: zodResolver(createSystemUserSchema),
    defaultValues: { name: "IAGENIX Ads", role: "ADMIN" },
  });
  const role = useWatch({ control: form.control, name: "role" });
  const criar = useMutation({
    mutationFn: (input: CreateSystemUserInput) =>
      api(`${base}/system-users`, { method: "POST", body: input }),
    onSuccess: async () => {
      await onDone();
      toast.success("System user criado na Meta.");
      form.reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={form.handleSubmit((v) => criar.mutate(v))} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Criar system user</DialogTitle>
            <DialogDescription>
              Criado de verdade no Business Manager. Admin acessa todos os ativos do BM; Funcionário
              só os que forem atribuídos.
            </DialogDescription>
          </DialogHeader>
          <FormField id="su-name" label="Nome" error={form.formState.errors.name?.message}>
            <Input id="su-name" autoFocus {...form.register("name")} />
          </FormField>
          <FormField id="su-role" label="Papel" error={form.formState.errors.role?.message}>
            <Select
              value={role}
              onValueChange={(v) =>
                form.setValue("role", v as CreateSystemUserInput["role"], { shouldValidate: true })
              }
            >
              <SelectTrigger id="su-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {META_SYSTEM_USER_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {META_BUSINESS_ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={criar.isPending}>
              Criar na Meta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Ativos ------------------------------------------------------------------------------------

function AssetsTab({
  detail,
  workspaceId,
  businessId,
  podeGerenciar,
}: {
  detail: BusinessDetailDto;
  workspaceId: string;
  businessId: string;
  podeGerenciar: boolean;
}) {
  const invalidate = useDetailInvalidate(workspaceId);
  const base = `/api/workspaces/${workspaceId}/meta/businesses/${businessId}`;
  const [atribuir, setAtribuir] = useState<BusinessAssetDto | null>(null);
  const [remover, setRemover] = useState<{
    asset: BusinessAssetDto;
    userId: string;
    name: string;
  } | null>(null);
  const removerMut = useMutation({
    mutationFn: (input: { asset: BusinessAssetDto; userId: string }) =>
      api(`${base}/assets/${input.asset.type}/${input.asset.metaId}/users/${input.userId}`, {
        method: "DELETE",
      }),
    onSuccess: async () => {
      await invalidate();
      setRemover(null);
      toast.success("Atribuição removida na Meta.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Contas, páginas e pixels do BM e quem tem tarefas em cada um. Atribua pessoas ou system
          users com as tarefas certas.
        </p>
        {detail.assets.length === 0 ? (
          <EmptyState
            title="Nenhum ativo"
            description="Este BM não tem contas, páginas ou pixels sincronizados."
          />
        ) : (
          <ul className="divide-y">
            {detail.assets.map((a) => (
              <li key={`${a.type}-${a.metaId}`} className="space-y-2 py-3">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {a.name} <Badge variant="outline">{META_ASSET_TYPE_LABELS[a.type]}</Badge>
                    </p>
                    <p className="text-xs text-muted-foreground">{a.subtitle}</p>
                  </div>
                  {podeGerenciar ? (
                    <Button variant="outline" size="sm" onClick={() => setAtribuir(a)}>
                      <Users />
                      Atribuir
                    </Button>
                  ) : null}
                </div>
                {a.assignedUsers.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {a.assignedUsers.map((u) => (
                      <li
                        key={u.id}
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs"
                      >
                        <span className="font-medium">{u.name}</span>
                        <span className="text-muted-foreground">
                          · {u.tasks.map(metaTaskLabel).join(", ") || "sem tarefas"}
                        </span>
                        {podeGerenciar ? (
                          <button
                            type="button"
                            className="ml-1 cursor-pointer text-muted-foreground hover:text-destructive"
                            aria-label={`Remover ${u.name} de ${a.name}`}
                            onClick={() => setRemover({ asset: a, userId: u.id, name: u.name })}
                          >
                            ×
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">Ninguém atribuído.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {atribuir ? (
        <AssignDialog
          open
          onOpenChange={(v) => {
            if (!v) setAtribuir(null);
          }}
          base={base}
          asset={atribuir}
          detail={detail}
          onDone={invalidate}
        />
      ) : null}
      <ConfirmDialog
        open={remover !== null}
        onOpenChange={(v) => {
          if (!v) setRemover(null);
        }}
        title={`Remover ${remover?.name ?? ""} de ${remover?.asset.name ?? ""}?`}
        description="A pessoa perde todas as tarefas neste ativo, na Meta."
        confirmLabel="Remover na Meta"
        destructive
        loading={removerMut.isPending}
        onConfirm={() => {
          if (remover) removerMut.mutate({ asset: remover.asset, userId: remover.userId });
        }}
      />
    </Card>
  );
}

function AssignDialog({
  open,
  onOpenChange,
  base,
  asset,
  detail,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  base: string;
  asset: BusinessAssetDto;
  detail: BusinessDetailDto;
  onDone: () => Promise<unknown>;
}) {
  const people = [
    ...detail.users
      .filter((u) => !u.pending)
      .map((u) => ({ id: u.id, label: `${u.name} (pessoa)` })),
    ...detail.systemUsers.map((s) => ({ id: s.id, label: `${s.name} (system user)` })),
  ];
  const [userId, setUserId] = useState(people[0]?.id ?? "");
  const [tasks, setTasks] = useState<string[]>(META_ASSET_TASKS[asset.type].slice(0, 1));
  const atribuir = useMutation({
    mutationFn: () =>
      api(`${base}/assets/${asset.type}/${asset.metaId}/users`, {
        method: "POST",
        body: { userId, tasks },
      }),
    onSuccess: async () => {
      await onDone();
      toast.success("Tarefas atribuídas na Meta.");
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const toggle = (t: string) =>
    setTasks((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (userId && tasks.length > 0) atribuir.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Atribuir {asset.name}</DialogTitle>
            <DialogDescription>
              Escolha a pessoa ou system user e as tarefas no{" "}
              {META_ASSET_TYPE_LABELS[asset.type].toLowerCase()}.
            </DialogDescription>
          </DialogHeader>
          <FormField id="assign-who" label="Quem">
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger id="assign-who">
                <SelectValue placeholder="Escolha" />
              </SelectTrigger>
              <SelectContent>
                {people.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Tarefas</legend>
            <div className="flex flex-wrap gap-3">
              {META_ASSET_TASKS[asset.type].map((t) => (
                <label key={t} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={tasks.includes(t)}
                    onChange={() => toggle(t)}
                  />
                  {metaTaskLabel(t)}
                </label>
              ))}
            </div>
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={atribuir.isPending}
              disabled={!userId || tasks.length === 0}
            >
              Atribuir na Meta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
